import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faExclamationTriangle } from '@fortawesome/pro-solid-svg-icons/faExclamationTriangle';
import type React from 'react';
import { useContext, useMemo } from 'react';
import { useSelector } from 'react-redux';
import { createSelector } from 'reselect';
import { FormattedMessage, FormattedList } from 'react-intl';
import classnames from 'classnames';

import { Text, TextType } from 'ui-components/components/text';
import { FlexRow } from 'ui-components/components/flex';

import Global from '../../../../../../store/global-selectors';
import { CustomsContext } from '../../../../context/context';
import WarningCurrencySegment from './currency-segment';
import type { CustomsContextValue, SalesOrder } from '../../../../types';

import messages from './messages';
import styles from '../common-status-style.module.less';

const getCurrencyMessage = (nonDefaultCurrencyList: string[], orderCurrencyCodes: string[], customsCurrencyCode: string | undefined) => {
  if (nonDefaultCurrencyList.length === 0 || !customsCurrencyCode) {
    return null;
  }

  const currencyCodeSegments = nonDefaultCurrencyList.map((currencyCode) => (
    <WarningCurrencySegment key={currencyCode} currencyCode={currencyCode} />
  ));

  const formattedCurrencyList = <FormattedList type='disjunction' value={currencyCodeSegments} />;
  const formattedCurrencyCode = <WarningCurrencySegment currencyCode={customsCurrencyCode} />;

  const formatMessage = (messageDescriptor: { id: string; defaultMessage: string; description: string }) => {
    return (
      <Text
        textType={TextType.Caption}
        value={
          <FormattedMessage
            {...messageDescriptor}
            values={{
              currencyList: formattedCurrencyList,
              customsCurrencyCode: formattedCurrencyCode,
            }}
          />
        }
      />
    );
  };

  if (orderCurrencyCodes.length === 1) {
    return formatMessage(messages.singleOrderWarning);
  }

  if (nonDefaultCurrencyList.length <= 2) {
    return formatMessage(messages.multiOrderMultiCurrencyWarning);
  }

  return formatMessage(messages.multiOrderSingleCurrencyWarning);
};

const salesOrderCurrencyCodesSelectorFactory = (fulfillmentPlanId: string) => {
  return createSelector(
    (state: unknown) =>
      Global.selectEntitiesSalesOrdersByFulfillmentPlanIds(state, {
        fulfillmentPlanIds: [fulfillmentPlanId],
      }),
    (salesOrders: SalesOrder[]) => {
      if (fulfillmentPlanId && !(fulfillmentPlanId.includes('rate') || fulfillmentPlanId.includes('customer'))) {
        const salesOrderCurrencies: string[] = salesOrders.map((salesOrder) => salesOrder.amountSummary.orderTotal.code || '');
        return salesOrderCurrencies;
      }
      return [];
    },
  );
};

interface CustomsWarningSectionProps {
  fulfillmentPlanId: string;
}

export const CustomsWarningSection: React.FC<CustomsWarningSectionProps> = ({ fulfillmentPlanId }) => {
  const orderCurrencyCodes: string[] = useSelector(salesOrderCurrencyCodesSelectorFactory(fulfillmentPlanId));
  const { customsItems = [] } = useContext(CustomsContext) as CustomsContextValue;
  const customsCurrencyCode = customsItems[0]?.unitPrice?.code;

  const nonDefaultCurrencyList = useMemo(() => {
    const uniqueNonDefaultCurrencies = new Set(orderCurrencyCodes.filter((code) => code !== customsCurrencyCode));
    return Array.from(uniqueNonDefaultCurrencies);
  }, [orderCurrencyCodes, customsCurrencyCode]);

  const message = getCurrencyMessage(nonDefaultCurrencyList, orderCurrencyCodes, customsCurrencyCode);

  if (!message) {
    return null;
  }

  return (
    <FlexRow className={styles['message-wrapper']}>
      <FontAwesomeIcon className={classnames(styles.icon, styles.warning)} icon={faExclamationTriangle} />
      {message}
    </FlexRow>
  );
};
